import React, { useCallback } from 'react';
import { ErrorMessage, Field, Formik, Form } from 'formik';
import FormFieldWithLabel from '../../../common/FormFieldWithLabel';
import CustomSelect from '../../../../common/formElements/CustomSelect';
import CustomLoader from '../../../common/CustomLoader';
import {
  createActionSettingsSchema,
  extractParameters,
  getFieldInfoByName,
} from '../collection/Util';
import { defaultFields } from 'drapcode-constant';
import { getValidationSchemaForTextAnonymization } from '../../../../common/formElements/CollectionSectionType';

const createSelectOption = (value, label) => ({ value, label });

const BaseAnonymizationForm = ({
  projectId,
  selectedEvent,
  eventAction,
  actionData,
  events,
  actions,
  hideModal,
  showLoaderForAction,
  version,
  showCustomTermsField = false,
}) => {
  const filterValidFields = useCallback((fields, excludeFieldName = '') => {
    return fields.filter((field) => {
      const lowercaseFieldName = field.fieldName.toLowerCase();
      return (
        defaultFields.indexOf(lowercaseFieldName) === -1 &&
        lowercaseFieldName !== 'createdby' &&
        field.fieldName !== excludeFieldName
      );
    });
  }, []);

  const getCollectionOptions = useCallback(() => {
    const defaultOption = [createSelectOption('', 'Choose Collection')];
    const collections = events && events.collections ? events.collections : [];

    if (collections.length === 0) return defaultOption;

    return defaultOption.concat(
      collections.map((collection) =>
        createSelectOption(collection.collectionName, collection.collectionTitle),
      ),
    );
  }, [events]);

  const getFieldOptions = useCallback(
    (collectionName, excludeFieldName = '', label = 'Choose Field') => {
      const defaultOption = [createSelectOption('', label)];
      if (!collectionName) return defaultOption;

      const collections = events && events.collections ? events.collections : [];
      const selectedCollection = collections.find((c) => c.collectionName === collectionName);

      if (!selectedCollection || !selectedCollection.fields) return defaultOption;

      const validFields = filterValidFields(selectedCollection.fields, excludeFieldName);

      return defaultOption.concat(
        validFields.map((field) => createSelectOption(field.fieldName, field.fieldTitle.en)),
      );
    },
    [events, filterValidFields],
  );

  const handleSubmit = (values) => {
    let parameters = extractParameters(values, actionData);
    let actionFormData = eventAction;
    actionFormData.parameters = parameters;
    actions.updateAction({
      projectId,
      version,
      eventId: selectedEvent.uuid,
      data: actionFormData,
    });
    hideModal();
  };

  const initialValues = React.useMemo(() => {
    return createActionSettingsSchema(actionData, eventAction);
  }, [actionData, eventAction]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={getValidationSchemaForTextAnonymization(showCustomTermsField)}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, setFieldTouched, isSubmitting, errors }) => (
        <Form>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  label="Choose Collection"
                  icon="select"
                  infoIcon
                  infoDescription="Select Collection which you want to Create Item For."
                >
                  <CustomSelect
                    name="collection"
                    isMulti={false}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    options={getCollectionOptions()}
                    value={values.collection}
                  />
                </FormFieldWithLabel>
                <ErrorMessage name="collection">
                  {(msg) => <p className="text-error ml-5">{msg}</p>}
                </ErrorMessage>
              </div>
            </div>

            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  label="Select source field"
                  icon="select"
                  infoIcon
                  infoDescription="Select Text field where your original text resides."
                >
                  <CustomSelect
                    name="fieldForSourceText"
                    isMulti={false}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    options={getFieldOptions(values.collection)}
                    value={values.fieldForSourceText}
                  />
                </FormFieldWithLabel>
                <ErrorMessage name="fieldForSourceText">
                  {(msg) => <p className="text-error ml-5">{msg}</p>}
                </ErrorMessage>
              </div>

              {showCustomTermsField && (
                <div className="form-group">
                  <FormFieldWithLabel
                    label="Select the field for additional terms to be anonymized"
                    icon="select"
                    infoIcon
                    infoDescription="Select the field containing your additional terms to anonymize."
                  >
                    <CustomSelect
                      name="fieldForCustomTerms"
                      isMulti={false}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      options={getFieldOptions(values.collection, values.fieldForSourceText)}
                      value={values.fieldForCustomTerms}
                    />
                  </FormFieldWithLabel>
                  <ErrorMessage name="fieldForCustomTerms">
                    {(msg) => <p className="text-error ml-5">{msg}</p>}
                  </ErrorMessage>
                </div>
              )}

              <div className="form-group">
                <FormFieldWithLabel
                  label="Select the destination field"
                  icon="select"
                  infoIcon
                  infoDescription="Select the text field where you want to save the anonymized text."
                >
                  <CustomSelect
                    name="fieldForAnonymizedText"
                    isMulti={false}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    options={getFieldOptions(values.collection, values.fieldForSourceText)}
                    value={values.fieldForAnonymizedText}
                  />
                </FormFieldWithLabel>
                <ErrorMessage name="fieldForAnonymizedText">
                  {(msg) => <p className="text-error ml-5">{msg}</p>}
                </ErrorMessage>
              </div>
            </div>

            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  label="Success Message"
                  icon="text"
                  infoIcon
                  infoDescription={getFieldInfoByName('successMessage')}
                >
                  <Field
                    name="successMessage"
                    type="text"
                    placeholder="(Optional) Enter custom success message."
                    className="form-control form-control-sm"
                  />
                </FormFieldWithLabel>
              </div>

              <div className="form-group">
                <FormFieldWithLabel
                  label="Error Message"
                  icon="text"
                  infoIcon
                  infoDescription={getFieldInfoByName('errorMessage')}
                >
                  <Field
                    name="errorMessage"
                    type="text"
                    placeholder="(Optional) Enter custom error message."
                    className="form-control form-control-sm"
                  />
                </FormFieldWithLabel>
              </div>
            </div>

            {errors.submit && (
              <div className="col-12">
                <p className="text-error">{errors.submit}</p>
              </div>
            )}
          </div>

          <div className="row mt-3">
            <div className="col-md-6 text-left">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={hideModal}
                disabled={isSubmitting}
              >
                <i className="wb webconnect-times" />
                &nbsp;Cancel
              </button>
            </div>
            <div className="col-md-6 text-right">
              <button type="submit" className="btn btn-theme-submit btn-sm" disabled={isSubmitting}>
                <i className="wb webconnect-save-regular" />
                &nbsp;Save&nbsp;
                <CustomLoader loading={showLoaderForAction || isSubmitting} />
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export const CustomTermsAnonymizationForm = (props) => {
  return <BaseAnonymizationForm {...props} showCustomTermsField={true} />;
};

export const NlpAnonymizationForm = (props) => {
  return <BaseAnonymizationForm {...props} showCustomTermsField={false} />;
};
